import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Header, ListBox, ListBoxItem, ListBoxSection, Popover, ProgressBar, Text } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { dedupeOptionsByValue } from './utils/dedupeOptions';
import { isOverlayTriggerReady } from './utils/overlayTrigger';

import styles from './AutocompletePopover.module.css';

type Option = {
  value: string | number | boolean;
  displayName?: string;
  resultsCount?: number;
  description?: string;
  category?: string;
  section?: string;
  iconName?: IconName;
  disabled?: boolean;
  /** Inline-rendered type chip (e.g. "string", "number") shown after the option label. */
  typeLabel?: string;
  /** Icon rendered at the right edge of the option (e.g. a green check next to a regex match). */
  trailingIconName?: IconName;
  /** Marker class applied to the trailing icon — `success` paints green, `danger` paints red. */
  trailingIconTone?: 'success' | 'danger';
  /**
   * When provided, replaces the rendered label content (the text inside the option's
   * label slot) with this React node. Use it for cases that need formatting inside
   * the label — e.g. wrapping a fragment in <code>. `displayName` is still used for
   * the accessible textValue and the highlight-match search text.
   */
  labelNode?: React.ReactNode;
  className?: string;
};

export type { Option as AutocompleteOption };

interface AutocompletePopoverProps {
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (value: string | number | boolean) => void;
  options: Option[];
  groupByCategory?: boolean;
  isLoading?: boolean;
  multiSelect?: boolean;
  selectedKeys?: string[];
  /** Index of the virtually-focused option driven by keyboard navigation. -1 or absent = none. */
  focusedIndex?: number;
  /** When provided, the matching substring in each option label is bolded. */
  searchText?: string;
  /** Applied to the Popover element for aria-controls linkage from the combobox trigger. */
  id?: string;
  /** Footer pinned below the options list, outside the scrolling area. Omitted = no footer. */
  footer?: React.ReactNode;
  header?: React.ReactNode;
  /**
   * Renders a metadata panel beside the options list, describing the option the user is
   * hovering or has highlighted with the arrow keys. Return `null` for options with nothing
   * to show (e.g. a "Search for value:" row) and the panel is left out entirely.
   */
  renderDetails?: (option: Option) => React.ReactNode;
  anchorKey?: string;
}

export const REFRESH_INDICATOR_DELAY_MS = 250;

function optionId(option: Option): string {
  return String(option.value);
}

/** A renderer that returns nothing renderable has no details for that option. */
function hasRenderableContent(node: ReactNode): boolean {
  return node !== null && node !== undefined && node !== false && node !== '';
}

/**
 * Which option the details panel describes, in order of preference: the row under the pointer, the
 * keyboard-highlighted row, the row described last render, then the first row with anything to show.
 * The final fallback keeps a reserved panel populated — an open panel is never blank.
 */
function pickDescribedOptionId(described: Map<string, ReactNode>, preferred: Array<string | null>): string | null {
  for (const id of preferred) {
    if (id !== null && described.has(id)) {
      return id;
    }
  }
  const [firstDescribed] = described.keys();
  return firstDescribed ?? null;
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) {
    return <>{text}</>;
  }
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, idx)}
      <strong className={styles.highlightedMatch}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}

function OptionItem({
  option,
  focused,
  searchText,
  descriptionInPanel,
}: {
  option: Option;
  focused?: boolean;
  searchText?: string;
  descriptionInPanel?: boolean;
}) {
  const label = option.displayName ?? String(option.value);
  const itemClass = [styles.item, focused && styles.itemFocused, option.className].filter(Boolean).join(' ');
  return (
    <ListBoxItem
      id={optionId(option)}
      className={itemClass}
      // Marks the virtually-focused option so the popover can scroll it into view.
      {...(focused ? { 'data-focused-option': '' } : {})}
      // Read by the popover's hover delegation to resolve which option the pointer is over.
      data-option-value={optionId(option)}
      textValue={label}
      isDisabled={option.disabled}
    >
      <Text slot="label" className={styles.label}>
        {option.iconName && <Icon name={option.iconName} size="small" />}
        <span>{option.labelNode ?? (searchText ? <HighlightMatch text={label} query={searchText} /> : label)}</span>
      </Text>
      {option.description && !descriptionInPanel && <Text slot="description">{option.description}</Text>}
      {option.typeLabel && <span className={styles.typeLabel}>{option.typeLabel}</span>}
      {option.resultsCount !== undefined && (
        <Text slot="description" className={styles.resultsCount}>
          {option.resultsCount.toLocaleString()}
        </Text>
      )}
      {option.trailingIconName && (
        <span
          className={
            option.trailingIconTone === 'success'
              ? `${styles.trailingIcon} ${styles.trailingIconSuccess}`
              : option.trailingIconTone === 'danger'
                ? `${styles.trailingIcon} ${styles.trailingIconDanger}`
                : styles.trailingIcon
          }
          aria-hidden="true"
        >
          <Icon name={option.trailingIconName} size="small" />
        </span>
      )}
    </ListBoxItem>
  );
}

function FlatList({
  options,
  onSelect,
  onOpenChange,
  multiSelect,
  selectedKeys,
  focusedIndex,
  searchText,
  describedIds,
}: Pick<
  AutocompletePopoverProps,
  'options' | 'onSelect' | 'onOpenChange' | 'multiSelect' | 'selectedKeys' | 'focusedIndex' | 'searchText'
> & { describedIds?: Pick<ReadonlySet<string>, 'has'> }) {
  if (multiSelect) {
    return (
      <ListBox
        aria-label={msg('queryBuilder.autocomplete.optionsLabel')}
        className={styles.listBox}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(keys) => {
          if (keys === 'all') {
            return;
          }
          const currentSet = new Set(selectedKeys ?? []);
          const newSet = new Set([...keys].map(String));
          const toggled = [...newSet].find((k) => !currentSet.has(k)) ?? [...currentSet].find((k) => !newSet.has(k));
          if (toggled) {
            const match = options.find((o) => optionId(o) === toggled);
            if (match) {
              onSelect(match.value);
            }
          }
        }}
      >
        {options.map((option, index) => (
          <OptionItem
            key={optionId(option)}
            option={option}
            focused={focusedIndex === index}
            searchText={searchText}
            descriptionInPanel={describedIds?.has(optionId(option))}
          />
        ))}
      </ListBox>
    );
  }

  return (
    <ListBox
      aria-label={msg('queryBuilder.autocomplete.optionsLabel')}
      className={styles.listBox}
      selectionMode="single"
      onSelectionChange={(keys) => {
        if (keys === 'all') {
          return;
        }
        const [key] = [...keys];
        if (key === null) {
          return;
        }
        const match = options.find((o) => optionId(o) === String(key));
        if (match) {
          onSelect(match.value);
          onOpenChange(false);
        }
      }}
    >
      {options.map((option, index) => (
        <OptionItem
          key={optionId(option)}
          option={option}
          focused={focusedIndex === index}
          searchText={searchText}
          descriptionInPanel={describedIds?.has(optionId(option))}
        />
      ))}
    </ListBox>
  );
}

function GroupedList({
  options,
  onSelect,
  onOpenChange,
  focusedIndex,
  searchText,
  describedIds,
}: Pick<AutocompletePopoverProps, 'options' | 'onSelect' | 'onOpenChange' | 'focusedIndex' | 'searchText'> & {
  describedIds?: Pick<ReadonlySet<string>, 'has'>;
}) {
  type Group = { key: string; category: string; options: Option[] };
  const groups: Group[] = [];
  for (const option of options) {
    const category = option.category ?? '';
    const key = option.section ?? category;
    const existing = groups.find((group) => group.key === key);
    if (existing) {
      existing.options.push(option);
    } else {
      groups.push({ key, category, options: [option] });
    }
  }

  // Map each option's stable id to its absolute index in the parent options array.
  // useAutocompleteNavigation tracks the flat index across all options, so the
  // grouped render needs to look up each option's true position rather than
  // assuming categories are contiguous (they may be interleaved).
  const flatIndexById = new Map(options.map((o, i) => [optionId(o), i]));

  return (
    <ListBox
      aria-label={msg('queryBuilder.autocomplete.optionsLabel')}
      className={styles.listBox}
      selectionMode="single"
      onSelectionChange={(keys) => {
        if (keys === 'all') {
          return;
        }
        const [key] = [...keys];
        if (key === null) {
          return;
        }
        const match = options.find((o) => optionId(o) === String(key));
        if (match) {
          onSelect(match.value);
          onOpenChange(false);
        }
      }}
    >
      {groups.map(({ key, category, options: groupOptions }) => (
        <ListBoxSection key={key}>
          {category && <Header>{category}</Header>}
          {groupOptions.map((option) => (
            <OptionItem
              key={optionId(option)}
              option={option}
              focused={flatIndexById.get(optionId(option)) === focusedIndex}
              searchText={searchText}
              descriptionInPanel={describedIds?.has(optionId(option))}
            />
          ))}
        </ListBoxSection>
      ))}
    </ListBox>
  );
}

export function AutocompletePopover({
  triggerRef,
  isOpen,
  onOpenChange,
  onSelect,
  options: rawOptions,
  groupByCategory = false,
  isLoading = false,
  multiSelect,
  selectedKeys,
  focusedIndex = -1,
  searchText,
  id,
  footer,
  header,
  renderDetails,
  anchorKey,
}: AutocompletePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  // The option the pointer is over, when any. Takes precedence over the keyboard highlight so
  // pointing at a row describes that row, matching how the list itself highlights on hover.
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  // Defensive net for direct callers: callers that drive keyboard navigation must
  // dedupe *before* wiring `focusedIndex` / `onSelect` (see dedupeOptionsByValue),
  // so this is normally a no-op on an already-deduped list.
  const options = dedupeOptionsByValue(rawOptions);

  const [triggerReady, setTriggerReady] = useState(false);
  useLayoutEffect(() => {
    if (!isOpen) {
      setTriggerReady(false);
      return undefined;
    }
    let raf = 0;
    let attempts = 0;
    const check = () => {
      if (isOverlayTriggerReady(triggerRef.current)) {
        setTriggerReady(true);
        window.dispatchEvent(new Event('resize'));
        return;
      }
      setTriggerReady(false);
      attempts += 1;
      if (attempts < 8) {
        raf = requestAnimationFrame(check);
      }
    };
    check();
    return () => cancelAnimationFrame(raf);
  }, [isOpen, triggerRef, anchorKey]);

  const popoverOpen = isOpen && triggerReady;
  const isRefreshing = isLoading && options.length > 0;
  const [isSlowRefresh, setIsSlowRefresh] = useState(false);
  useEffect(() => {
    if (!isRefreshing) {
      setIsSlowRefresh(false);
      return undefined;
    }
    const timeout = setTimeout(() => setIsSlowRefresh(true), REFRESH_INDICATOR_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isRefreshing]);

  // Keyboard navigation is virtual (focus stays on the input), so the ListBox
  // never auto-scrolls. When the highlighted option changes, pull it into view
  // so ArrowUp/Down past the visible window scrolls the popover with the highlight.
  // Deferred a frame because the popover renders in a portal that mounts after this
  // effect; scope the lookup to the `id` container (falling back to the popover ref).
  useEffect(() => {
    if (focusedIndex < 0) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      const root: ParentNode | null = (id ? document.getElementById(id) : null) ?? ref.current;
      root?.querySelector<HTMLElement>('[data-focused-option]')?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedIndex, id]);

  // Nudge RA to re-measure when an ancestor resizes (e.g. nav collapse slides the trigger sideways); RA otherwise only tracks resize/scroll/element-size, not position moves.
  useEffect(() => {
    if (!popoverOpen || typeof ResizeObserver === 'undefined') {
      return;
    }
    const trig = triggerRef.current;
    if (!trig) {
      return;
    }
    let initialized = false;
    const observer = new ResizeObserver(() => {
      // Skip ResizeObserver's initial observe() fire; only react to real post-open layout changes.
      if (!initialized) {
        initialized = true;
        return;
      }
      window.dispatchEvent(new Event('resize'));
    });
    for (let el: HTMLElement | null = trig; el; el = el.parentElement) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [popoverOpen, triggerRef, anchorKey]);

  // Content for every option the consumer can describe, resolved once per render. The panel column
  // is reserved for the whole open list rather than for the highlighted row alone: rows the renderer
  // skips (AND / OR, group prefixes, the pinned "Search for …" row) would otherwise collapse the
  // panel and the next field would snap it open again, resizing the popover under the pointer.
  // Renderers run for every option, so they must stay cheap and side-effect free — put any
  // per-option fetching inside the node they return, which only mounts for the described option.
  const describedOptions = useMemo(() => {
    const described = new Map<string, ReactNode>();
    if (!renderDetails) {
      return described;
    }
    for (const option of options) {
      const content = renderDetails(option);
      if (hasRenderableContent(content)) {
        described.set(optionId(option), content);
      }
    }
    return described;
  }, [options, renderDetails]);

  const focusedId = focusedIndex >= 0 && options[focusedIndex] ? optionId(options[focusedIndex]) : null;
  // Arrow keys move the panel even while the pointer rests on a row: a stationary cursor left over
  // from an earlier hover would otherwise pin the panel and make keyboard navigation look dead.
  useEffect(() => {
    setHoveredValue(null);
  }, [focusedIndex]);

  // What the panel described last render, tied to the list it was describing. Holding it lets a
  // highlight passing over a row with no details leave the panel as it was instead of blanking a
  // column that is still on screen; tying it to the list means a newly opened or re-filtered list
  // starts on its own first describable row rather than one the pointer left behind.
  const listSignature = [...describedOptions.keys()].join('\n');
  const stickyRef = useRef<{ listSignature: string; id: string } | null>(null);
  const stickyId = stickyRef.current?.listSignature === listSignature ? stickyRef.current.id : null;
  const describedId = pickDescribedOptionId(describedOptions, [hoveredValue, focusedId, stickyId]);
  useEffect(() => {
    if (describedId !== null) {
      stickyRef.current = { listSignature, id: describedId };
    }
  }, [describedId, listSignature]);
  const details = describedId === null ? null : describedOptions.get(describedId);
  const hasDetailsPanel = describedOptions.size > 0;

  const hoverProps = renderDetails
    ? {
        // Tracked on mouse *move* rather than mouseover: scrolling the keyboard-highlighted option
        // into view slides rows under a stationary cursor, which fires mouseover without the user
        // having pointed at anything.
        onMouseMove: (e: React.MouseEvent) => {
          // `e.target` is an EventTarget — narrow to Element before reaching for DOM APIs rather
          // than casting, since a pointer event can land on a non-Element node.
          if (!(e.target instanceof Element)) {
            return;
          }
          const { optionValue } = e.target.closest<HTMLElement>('[data-option-value]')?.dataset ?? {};
          if (optionValue !== undefined && optionValue !== hoveredValue) {
            setHoveredValue(optionValue);
          }
        },
        onMouseLeave: () => setHoveredValue(null),
      }
    : {};

  return (
    <Popover
      key={anchorKey}
      triggerRef={triggerRef}
      isOpen={popoverOpen}
      onOpenChange={onOpenChange}
      placement="bottom start"
      // Always render below the filter bar; never flip up.
      shouldFlip={false}
      offset={16}
      className={clsx(
        styles.popover,
        hasDetailsPanel && styles.popoverWithDetails,
        options.some((o) => o.section !== undefined) && styles.popoverWithActions,
      )}
      aria-label={msg('queryBuilder.autocomplete.popoverLabel')}
      isNonModal
      ref={ref}
    >
      <div className={styles.body} {...hoverProps}>
        <div id={id} className={styles.optionsColumn}>
          {header && (
            // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keep focus on the input
            <div className={styles.header} onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
              {header}
            </div>
          )}
          {isLoading && options.length === 0 && (
            <div className={styles.loading}>
              <ProgressBar aria-label={msg('queryBuilder.autocomplete.loadingLabel')} isIndeterminate />
            </div>
          )}
          {isRefreshing && isSlowRefresh && (
            <div className={styles.refreshing}>
              <ProgressBar aria-label={msg('queryBuilder.autocomplete.loadingLabel')} isIndeterminate />
            </div>
          )}
          {options.length === 0 ? (
            !isLoading && (
              <div className={styles.empty}>
                <Message id="queryBuilder.autocomplete.noOptions" />
              </div>
            )
          ) : groupByCategory ? (
            <GroupedList
              options={options}
              onSelect={onSelect}
              onOpenChange={onOpenChange}
              focusedIndex={focusedIndex}
              searchText={searchText}
              describedIds={describedOptions}
            />
          ) : (
            <FlatList
              options={options}
              onSelect={onSelect}
              onOpenChange={onOpenChange}
              multiSelect={multiSelect}
              selectedKeys={selectedKeys}
              focusedIndex={focusedIndex}
              searchText={searchText}
              describedIds={describedOptions}
            />
          )}
        </div>
        {hasDetailsPanel && (
          // Same guards as the footer: keep DOM focus on the input so reading the panel doesn't
          // tear the popover down, and keep clicks from reaching the bar's dismiss handlers.
          // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- propagation guard only, not an interactive control
          <div
            className={styles.details}
            aria-live="polite"
            aria-atomic="true"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            {details}
          </div>
        )}
      </div>
      {footer && (
        // Keep focus on the input (preventDefault) so the popover isn't torn down before the
        // click lands, and stop the click reaching the bar/overlay dismiss handlers.
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- propagation guard only, not an interactive control
        <div className={styles.footer} onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      )}
    </Popover>
  );
}

AutocompletePopover.displayName = 'AutocompletePopover';
